import clsx from "clsx";
import { differenceInSeconds } from "date-fns";

import { getSelectedTools } from "../tools";
import type { ToolType } from "../tools/types";

import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY, DEFAULT_MODEL_FOR_CHAT } from "./AISettings";

import { AIModelType } from "components/server-types";
import { Button } from "mds/components/Button";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { showConfirmationModal } from "mds/components/Modal";
import { Tooltip } from "mds/components/Tooltip";
import { useHoverable } from "mds/hooks/use-hoverable";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CircleExclamationIcon,
  LoadingDotsIcon,
  RefreshIcon,
} from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentUserIsMPAdmin, selectUserSelectedAIModel } from "store/selectors";
import {
  selectContextFiles,
  selectContextMessages,
  selectIsAiDebuggerVisible,
  selectToolNames,
} from "store/selectors/ai";
import {
  AIContextItem,
  AIFileType,
  AIToolMessage,
  AIToolResponseMessage,
  aiStateActions,
} from "store/slices/ai";
import { isAiDebuggerVisible } from "utils/waffle-flags";

// This is input pricing. Output pricing is usually 4x.
const PRICE_PER_MODEL: Record<AIModelType, number> = {
  "gpt-4.1-nano": 0.1,
  "gpt-4.1": 2.0,
  "gpt-5-nano": 0.05,
  "gpt-5-mini": 0.25,
  "gpt-5": 1.25,
  "claude-3-5-haiku-latest": 0.8,
  "claude-sonnet-4-0": 3.0,
  "claude-opus-4-1": 15.0,
  "o4-mini": 1.1,
};
// We only output ~10% as much as we input, so we use a multiplier on the input
// tokens to estimate pricing for the total request.
const AVERAGE_OUTPUT_TO_INPUT_RATIO = 1.2;

// As of Sep 2025, average chars per token for english chat requests is 4
const AVERAGE_CHARS_PER_TOKEN = 4;

// As of Sep 2025, windows are 200k-400k normally, and we use the lower bound
const MIN_TOKEN_WINDOW = 200_000;

const estimateRequestCost = (requestSize: number, model: AIModelType) => {
  // This functionality mainly serves to remind developers that AI is really cheap and we
  // should not be stingy with the context we provide.
  const tokens = Math.ceil(requestSize / AVERAGE_CHARS_PER_TOKEN);
  const contextPercent = Math.round((tokens / MIN_TOKEN_WINDOW) * 100);
  const price = (tokens / 1_000_000) * PRICE_PER_MODEL[model] * AVERAGE_OUTPUT_TO_INPUT_RATIO;
  const priceString = `~$${price.toFixed(5)}`;
  return { tokens, contextPercent, price: priceString };
};

export const AIAssistantDebugger = () => {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector(selectIsAiDebuggerVisible);

  const icon = expanded ? <ChevronUpIcon /> : <ChevronDownIcon />;
  const aiModel = useAppSelector(selectUserSelectedAIModel) || DEFAULT_MODEL_FOR_CHAT;

  const contextMessages = useAppSelector(selectContextMessages);
  const toolNames = useAppSelector(selectToolNames);
  const tools = getSelectedTools(toolNames);
  const contextFiles = useAppSelector(selectContextFiles);

  const totalSize = contextMessages.map((item) => item.text).join("\n").length;
  const { tokens, contextPercent, price } = estimateRequestCost(totalSize, aiModel);
  const assessmentPrice = estimateRequestCost(totalSize, DEFAULT_MODEL_FOR_ASSESSMENT_QUERY).price;
  const assessmentModel = DEFAULT_MODEL_FOR_ASSESSMENT_QUERY;

  const openAllContextModal = () => {
    const allContextItems = [...contextMessages];

    showConfirmationModal({
      title: "AI Context",
      hideCancelConfirmButtons: true,
      className: "!w-[80vw] !max-w-[80vw]",
      children: (
        <div className="max-h-[70vh] overflow-y-auto px-1">
          {allContextItems.map((item) => {
            return (
              <div
                className="border-neutral-200 border-t py-2 first:border-t-0 first:pt-0"
                key={`${item.id}`}
              >
                <div className="mb-1 text-sm font-medium">
                  {item.id} ({item.role})
                </div>
                <pre aria-label="ai-context-modal-content" className="whitespace-pre-wrap text-xs">
                  {item.text}
                </pre>
              </div>
            );
          })}
          <HorizontalDivider />
          <h4 className="pt-4">Available Tools</h4>
          {tools.length > 0 ? (
            <div className="flex flex-col items-start gap-1 rounded-md">
              {tools.map((tool) => (
                <div key={tool.name}>
                  <div className="font-bold">{tool.name}</div>
                  <div className="text-sm">{tool.description}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-start gap-1 rounded-md">
              <div className="py-1 text-sm">None</div>
            </div>
          )}
        </div>
      ),
    });
  };

  return (
    <div className="rounded-md bg-yellow-tint-90 p-2 text-sm">
      <h4>
        <button
          className="flex w-full items-center justify-between"
          type="button"
          onClick={() => dispatch(aiStateActions.setAiDebuggerVisible(!expanded))}
        >
          <span>AI Assistant Debugger</span>
          <span className="mr-2">{icon}</span>
        </button>
      </h4>
      {expanded && (
        <div className="flex flex-col gap-2 pt-4">
          <h4>Base Context</h4>
          <div className="flex flex-col items-start gap-1 rounded-md px-4">
            <button
              className="w-full cursor-pointer text-left"
              type="button"
              onClick={openAllContextModal}
            >
              {contextMessages.length === 0 && <div className="py-1 text-sm">None</div>}
              {contextMessages.map((item) => (
                <AIContextInfoItem chatMessage={item} key={item.id} />
              ))}
            </button>
          </div>
          <h4>Available Tools</h4>
          {tools.length > 0 ? (
            <button
              className="w-full cursor-pointer text-left"
              type="button"
              onClick={openAllContextModal}
            >
              <div className="flex flex-col items-start gap-1 rounded-md px-4">
                {tools.map((tool) => (
                  <AIToolCallItem key={tool.name} tool={tool} />
                ))}
              </div>
            </button>
          ) : (
            <div className="py-1 text-sm">None</div>
          )}
          <Tooltip content="A limitation of the file context is that it does not show which files would be used for assessment queries.">
            <h4>Files</h4>
          </Tooltip>
          <div className="flex flex-col items-start gap-1 rounded-md px-4">
            {contextFiles.length > 0 ? (
              contextFiles.map((file) => <AIFileItem file={file} key={file.url} />)
            ) : (
              <div className="flex flex-col items-start gap-1 rounded-md px-4">
                <div className="py-1 text-sm">None</div>
              </div>
            )}
          </div>
          <h4>Model info</h4>
          <div className="flex flex-col items-start gap-1 rounded-md px-4">
            <div className="w-full py-1">Model: {aiModel}</div>
            <div className="w-full py-1">
              Tokens: {tokens}/{MIN_TOKEN_WINDOW} ({contextPercent}%)
            </div>
            <Tooltip
              content={`This is an estimate of the cost of the request if send through chat with the currently selected model (${aiModel}). For AI actions like assessment, we use ${assessmentModel}, which would cost ${assessmentPrice} for the same context size.`}
            >
              <div className="w-full py-1">Cost: {price}</div>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export const AIContextInfoItem = ({ chatMessage }: { chatMessage: AIContextItem }) => {
  const contextSize = chatMessage.text.length;
  const kB = contextSize / 1024;
  return (
    <div className="w-full py-1 text-sm">
      <Tooltip content={chatMessage.text} wrapperClassName="w-full">
        <div className="flex w-full items-center justify-between gap-1">
          <div className="">
            {chatMessage.id} ({chatMessage.role})
          </div>
          <div className="">{kB <= 1 ? kB.toFixed(1) : Math.round(kB)} kb</div>
        </div>
      </Tooltip>
    </div>
  );
};

export const AIToolCallItem = ({ tool }: { tool: ToolType }) => {
  return (
    <Tooltip content={tool.description} wrapperClassName="w-full">
      <div className="w-full py-1 text-sm">{tool.name}</div>
    </Tooltip>
  );
};

export const AIFileItem = ({ file }: { file: AIFileType }) => {
  const description = `Type: ${file.content_type}\n\nURL: ${file.url}\n\nSource: ${file.source}${file.error ? `\n\nError: ${file.error}` : ""}`;
  return (
    <div className="w-full py-1 text-sm">
      <Tooltip content={description}>
        <div className={clsx(file.error ? "line-through" : "")}>{file.name}</div>
      </Tooltip>
    </div>
  );
};

export const AIToolCallOutputItem = ({
  functionCall,
  functionCallOutput,
}: {
  functionCall: AIToolMessage;
  functionCallOutput?: AIToolResponseMessage;
}) => {
  const { isHovering, hoverParentProps } = useHoverable();
  const isMPadmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const canSeeAIDebugger = isAiDebuggerVisible();
  const finished = functionCallOutput?.finishedAt || functionCallOutput?.error;
  const failed = functionCallOutput?.error;
  const toolNames = useAppSelector(selectToolNames);
  const tools = getSelectedTools(toolNames);
  const duration = functionCallOutput?.finishedAt
    ? differenceInSeconds(functionCallOutput.finishedAt, functionCall.createdAt)
    : null;
  const toolArgs = functionCall.arguments;
  const toolOutputContext = functionCallOutput?.output;
  const tooltipContent = [
    failed ? ["Error:", JSON.stringify(functionCallOutput.error, null, 2)].join("\n") : null,
    ["Tool called with:", JSON.stringify(toolArgs, null, 2)].join("\n"),
    [
      "Tool output:",
      JSON.stringify(toolOutputContext, null, 2)?.slice(0, 200),
      "(...truncated to 200 characters)",
    ].join("\n"),
  ]
    .filter(Boolean)
    .join("\n\n");

  const retryToolCall = () => {
    const tool = tools.find((t) => t.name === functionCall.name);
    if (!tool) {
      alert(`Tool ${functionCall.name} not found`);
      return;
    }

    tool.callback(toolArgs);
  };

  const Message = (
    <div
      className="relative m-2 flex w-full items-center justify-start gap-2 text-sm text-black-tint-40"
      {...(canSeeAIDebugger ? hoverParentProps : {})}
    >
      {failed ? <CircleExclamationIcon /> : finished ? <CircleCheckIcon /> : <LoadingDotsIcon />}
      <span>
        {failed
          ? `Action failed: ${JSON.stringify(functionCallOutput.error)}`
          : functionCall.status}
      </span>
      {typeof duration === "number" && !isNaN(duration) && duration !== 0 && (
        <span className="ml-4">({duration}s)</span>
      )}
      {isMPadmin && isHovering && (
        <Button
          className="absolute right-0 gap-2 p-2"
          kind="white"
          size="xs"
          onClick={() => retryToolCall()}
        >
          <RefreshIcon /> <span>Re-apply</span>
        </Button>
      )}
    </div>
  );

  if (canSeeAIDebugger) {
    return (
      <Tooltip
        {...(canSeeAIDebugger ? hoverParentProps : {})}
        content={tooltipContent}
        key={functionCall.id}
        wrapperClassName="w-full"
      >
        {Message}
      </Tooltip>
    );
  }
  return Message;
};
